import { useCallback, useState } from "react";

import { Condition } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { useEncounterProcedures } from "@/encounters/contexts/EncounterProceduresContext";
import { setDiagnosisProblemListMembership } from "@/encounters/services/diagnosis-problem-list";
import { isProblemListDiagnosis } from "@/encounters/utils/diagnosis-problem-list";
import { useAppDispatch } from "@/store";
import { removeProblem, upsertProblems } from "@/store/slices/client-problems";
import { getCodeableConceptCode } from "@/utils/medplum";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

export type UseDiagnosisProblemList = {
  /**
   * The diagnosis a write is in flight for, so its card can say so and refuse a second click. It
   * stays set until the encounter has been re-read, because that read is what changes the label.
   */
  pendingConditionId: string | undefined;
  /** Puts the diagnosis on the client's problem list, or takes it off if it is already on it. */
  toggleProblemList: (condition: Condition) => void;
};

/**
 * The Dx tab's `Add to Problem List` / `In Problem List` control.
 *
 * Unlike the rest of the card, this writes as soon as it is clicked: the problem list is a record
 * about the client, and the panel that shows it is not waiting for this encounter to be saved.
 *
 * Two things happen after the write. The client's problems in the store are updated, because the side
 * panel reads them from there and its poll never drops a problem it stops seeing. And the encounter is
 * re-read, because the membership is a fact about the diagnosis resource — re-reading is what flips
 * the card, and it keeps what the tab shows and what the server holds as one answer rather than two.
 * The diagnosis stays pending until that re-read finishes, so the control cannot be used again while
 * the card is still showing the membership the write replaced.
 */
function useDiagnosisProblemList(): UseDiagnosisProblemList {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const { reload } = useEncounterProcedures();
  const [pendingConditionId, setPendingConditionId] = useState<string>();

  const toggleProblemList = useCallback(
    (condition: Condition) => {
      // A second click while the first is still in flight would race the two writes against each
      // other, and both were built from the same starting state.
      if (!condition.id || pendingConditionId) return;

      const isMember = !isProblemListDiagnosis(condition);
      const diagnosisCode = getCodeableConceptCode(condition.code);

      const run = async () => {
        setPendingConditionId(condition.id);

        // Cleared in one place, after every path this can take, because the failure paths return
        // early and the success path has the re-read still to wait for.
        try {
          const result = await setDiagnosisProblemListMembership({ medplum, condition, isMember });

          if (!result.ok) {
            if (result.reason === "unsaved-diagnosis") {
              // The control is disabled for a diagnosis the encounter has not written, so this is the
              // contract holding rather than a state a provider reaches.
              toast({
                type: "warning",
                title: "Save the encounter first",
                message:
                  "This diagnosis has not been saved yet, so it cannot go on the client's problem list.",
                autoClose: 5000,
              });
              return;
            }

            // Only the diagnosis's id reaches Sentry. What the client is diagnosed with is clinical.
            captureExceptionWithoutPhi(
              "Failed to change a diagnosis's problem-list membership",
              result.details,
              { conditionId: condition.id },
            );
            toast({
              type: "error",
              title: "Failed to update the problem list",
              message:
                "The client's problem list was not changed. Try again, or contact support if the error persists.",
              autoClose: 5000,
            });
            return;
          }

          // The store is what the client side panel renders, so it is told outright rather than left to
          // the poll: an addition would arrive seconds late, and a removal would never arrive at all.
          dispatch(isMember ? upsertProblems([result.data]) : removeProblem(result.data.id!));

          toast({
            type: "success",
            title: isMember ? "Added to problem list" : "Removed from problem list",
            message: isMember
              ? `${diagnosisCode} is now on the client's problem list.`
              : `${diagnosisCode} is no longer on the client's problem list.`,
            autoClose: 3000,
          });

          // Awaited so the diagnosis stays pending until the card has the new membership. Clearing
          // when the write returned re-enabled the button while the card still read the old way, and
          // a second click in that window computed the same change again from the stale copy.
          await reload();
        } finally {
          setPendingConditionId(undefined);
        }
      };

      void run();
    },
    [dispatch, medplum, pendingConditionId, reload],
  );

  return { pendingConditionId, toggleProblemList };
}

export default useDiagnosisProblemList;
